import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IOcbCUEnvironmentRecipient, IOcbEnvironmentRecipient, IOcbNotificationGroup, IOcbUser, NotificationsService, OcbEnvironmentLogTypes, OcbFileTransferLogTypes, OcbRemotePlatformLogTypes } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent, DxValidationGroupComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-alerts',
    styleUrls: ['./alerts.component.scss'],
    templateUrl: './alerts.component.html',
})
export class AlertsComponent implements AfterViewInit {
    @ViewChild('groupsGrid')
    public set groupsGridPlaceholder(placeholder: DxDataGridComponent) {
        if (placeholder != null) {
            this.groupsGrid = placeholder;
        }
    }

    @ViewChild('recipientsGrid')
    public set recipientsGridPlaceholder(placeholder: DxDataGridComponent) {
        if (placeholder != null) {
            this.recipientsGrid = placeholder;
        }
    }

    @ViewChild('groupValidation')
    public groupValidationGroup: DxValidationGroupComponent;

    @ViewChild('recipientValidation')
    public recipientValidationGroup: DxValidationGroupComponent;

    public environmentId: string = '';
    public activeTab: 'groups' | 'recipients' = 'recipients';

    public createGroupVisible = false;
    public editGroupVisible = false;
    public createRecipientVisible = false;

    public groups: IOcbNotificationGroup[];
    public recipients: IOcbEnvironmentRecipient[];
    public environmentLogTypes: string[];
    public environmentLogTypesLabels: any[];
    public fileTransferLogTypes: string[];
    public fileTransferLogTypesLabels: any[];
    public remotePlatformLogTypes: string[];
    public remotePlatformLogTypesLabels: any[];

    public newRecipientData: IOcbCUEnvironmentRecipient;
    public editedRecipients: IEnvironmentRecipientUpdateObject[] = [];

    public newGroupData: INotificationGroupData;
    public editGroupData: INotificationGroupData;

    private groupsGrid: DxDataGridComponent;
    private recipientsGrid: DxDataGridComponent;

    constructor(
        private notifsService: NotificationsService,
        private route: ActivatedRoute,
        private translateService: TranslateService) {
        this.displayEnvironmentLogType = this.displayEnvironmentLogType.bind(this);
        this.displayFileTransferLogType = this.displayFileTransferLogType.bind(this);
        this.displayRemotePlatformLogType = this.displayRemotePlatformLogType.bind(this);
    }

    public ngAfterViewInit(): void {
        forkJoin(
            this.translateService.get('environmentLogTypes'),
            this.translateService.get('fileTransferLogTypes'),
            this.translateService.get('remotePlatformLogTypes'),
        ).subscribe(
            messages => {
                this.environmentLogTypesLabels = messages[0];
                this.fileTransferLogTypesLabels = messages[1];
                this.remotePlatformLogTypesLabels = messages[2];
            },
            err => this.handleError(err),
        );

        this.route.params.subscribe(_ => {
            this.environmentId = this.route.snapshot.params.id;
            this.loadData();
        });
    }

    /**
     * Set Groups context menu items
     * @param e Handle event
     */
    public onGroupsContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.edit'),
                this.translateService.get('_commons.delete'),
            ).subscribe(
                messages => {
                    e.items = [
                        { text: messages[0], icon: 'edit', onItemClick: (ev) => this.showGroupEditPopup(ev, e.row.data) },
                        { text: messages[1], icon: 'trash', onItemClick: (ev) => this.deleteGroup(ev, e.row.data) },
                    ];
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Show edit popup for selected group
     * @param e Handle event
     */
    public onGroupsRowDblClick(e) {
        if (e && e.data) {
            this.showGroupEditPopup(e, e.data);
        }
    }

    /**
     * Set Recipients context menu items
     * @param e Handle event
     */
    public onRecipientsContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.delete'),
            ).subscribe(
                messages => {
                    e.items = [
                        { text: messages[0], icon: 'trash', onItemClick: (ev) => this.deleteRecipient(ev, e.row.data) },
                    ];
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Hides the selection dropdown in tag box
     */
    public hideTagBoxDropdown(e): void {
        e.component.option('openOnFieldClick', false);
    }

    public showTagBoxDropButton(e): void {
        e.component.option('showDropDownButton', true);
    }

    /**
     * Concatenate all group's emails
     * @param group Source group
     */
    public concatRecipients(group: IOcbNotificationGroup) {
        return group.recipients.map(rec => rec.emailAddress).join(';');
    }

    // GROUPS TAB //

    /**
     * Displays the group creation popup
     */
    public showGroupCreatePopup(): void {
        this.newGroupData = {
            name: '',
            environmentId: this.environmentId,
            emails: [],
            users: [],
        };

        this.createGroupVisible = true;
    }

    /**
     * Displays the group edition popup
     */
    public showGroupEditPopup(e, source: IOcbNotificationGroup): void {
        if (e && e.event) {
            e.event.stopPropagation();
        }

        const emails = source.recipients
            .filter(recipient => recipient.emailAddress)
            .map(recipient => recipient.emailAddress);

        const users = source.recipients
            .filter(recipient => !recipient.emailAddress)
            .map(recipient => recipient.user);

        this.editGroupData = {
            id: source.id,
            environmentId: this.environmentId,
            name: source.name,
            emails,
            users,
        };

        this.editGroupVisible = true;
    }

    /**
     * Creates a new notification group.
     */
    public createGroup(): void {
        if (this.groupValidationGroup.instance.validate().isValid) {
            this.createGroupVisible = false;
            const newGroup = this.mapDataToGroup(this.newGroupData);
            this.showLoading$(this.groupsGrid, '_commons.loading')
                .pipe(
                    mergeMap(() => this.notifsService.addNotificationGroup(newGroup)),
                    mergeMap(_ => this.translateService.get('alerts.msgAddGroupComplete')),
                    finalize(() => this.groupsGrid.instance.endCustomLoading()),
                )
                .subscribe(
                    message => {
                        this.loadData();
                        notify(message, 'success', 3000)
                    },
                    err => this.handleError(err),
                );
        }
    }

    /**
     * Updates a notification group.
     */
    public editGroup(): void {
        this.editGroupVisible = false;
        const updatedGroup = this.mapDataToGroup(this.editGroupData);

        this.showLoading$(this.groupsGrid, '_commons.loading')
            .pipe(
                mergeMap(() => this.notifsService.updateNotificationGroup(updatedGroup.id, updatedGroup)),
                mergeMap(_ => this.translateService.get('alerts.msgUpdateGroupComplete')),
                finalize(() => this.groupsGrid.instance.endCustomLoading()),
            )
            .subscribe(
                message => {
                    this.loadData();
                    notify(message, 'success', 3000);
                },
                err => this.handleError(err),
            );
    }

    /**
     * Deletes a notification group after confirmation.
     * @param group Group to delete.
     */
    public deleteGroup(e, group: IOcbNotificationGroup): void {
        e.event.stopPropagation();
        forkJoin(
            this.showLoading$(this.groupsGrid, '_commons.loading'),
            this.translateService.get('alerts.msgDeleteGroupConfirm', { name: group.name }),
            this.translateService.get('alerts.msgDeleteGroupConfirmTitle'),
        ).pipe(
            mergeMap(messages => fromPromise(confirm(messages[1], messages[2]))),
            filter(accepted => accepted),
            mergeMap(_ => this.notifsService.deleteNotificationGroup(group.id)),
            mergeMap(_ => this.translateService.get('alerts.msgDeleteGroupComplete')),
            finalize(() => this.groupsGrid.instance.endCustomLoading()),
        ).subscribe(
            message => {
                this.loadData();
                notify(message, 'success', 3000);
            },
            err => this.handleError(err),
        );
    }

    // RECIPIENTS TAB //

    /**
     * Displays the recipient creation popup
     */
    public showRecipientCreatePopup(): void {
        this.newRecipientData = {
            emailAddress: null,
            userId: null,
            notificationGroupId: null,
            environmentLogTypesEnums: [],
            fileTransferLogTypesEnums: [],
            remotePlatformLogTypesEnums: [],
        };

        this.createRecipientVisible = true;
    }

    /**
     * Adds or removes a environment log type from a recipient
     * @param recipient Recipient to update
     * @param value Log type value to add / remove
     * @param e Event object, containing a boolean to add or remove
     */
    public setRecipientEnvironmentLogValue(recipient: IOcbEnvironmentRecipient, value: OcbEnvironmentLogTypes, e: { value: boolean }): void {
        if (e.value) {
            recipient.environmentLogTypesEnums.push(value);
        }
        else {
            const index = recipient.environmentLogTypesEnums.indexOf(value);
            recipient.environmentLogTypesEnums.splice(index, 1);
        }

        this.setEditedRecipient(recipient);
    }

    /**
     * Adds or removes a file transfers log type from a recipient
     * @param recipient Recipient to update
     * @param value Log type value to add / remove
     * @param e Event object, containing a boolean to add or remove
     */
    public setRecipientFileTransferLogValue(recipient: IOcbEnvironmentRecipient, value: OcbFileTransferLogTypes, e: { value: boolean }): void {
        if (e.value) {
            recipient.fileTransferLogTypesEnums.push(value);
        }
        else {
            const index = recipient.fileTransferLogTypesEnums.indexOf(value);
            recipient.fileTransferLogTypesEnums.splice(index, 1);
        }

        this.setEditedRecipient(recipient);
    }

    /**
     * Adds or removes a remote platform log type from a recipient
     * @param recipient Recipient to update
     * @param value Log type value to add / remove
     * @param e Event object, containing a boolean to add or remove
     */
    public setRecipientRemotePlatformLogValue(recipient: IOcbEnvironmentRecipient, value: OcbRemotePlatformLogTypes, e: { value: boolean }): void {
        if (e.value) {
            recipient.remotePlatformLogTypesEnums.push(value);
        }
        else {
            const index = recipient.remotePlatformLogTypesEnums.indexOf(value);
            recipient.remotePlatformLogTypesEnums.splice(index, 1);
        }

        this.setEditedRecipient(recipient);
    }

    /**
     * Creates a new notification group.
     */
    public createRecipient(): void {
        if (this.recipientValidationGroup.instance.validate().isValid) {
            this.createRecipientVisible = false;
            this.showLoading$(this.recipientsGrid, '_commons.loading')
                .pipe(
                    mergeMap(() => this.notifsService.addEnvironmentRecipient(this.environmentId, this.newRecipientData)),
                    tap(recipient => this.recipients.push(recipient)),
                    mergeMap(_ => this.translateService.get('alerts.msgAddRecipientComplete')),
                    finalize(() => this.recipientsGrid.instance.endCustomLoading()),
                )
                .subscribe(
                    message => notify(message, 'success', 3000),
                    err => this.handleError(err),
                );
        }
    }

    /**
     * Update multiple recipients
     * @param recipients Array of recipients
     */
    public updateRecipients(): void {
        // Create a service request Observable for each updated items
        const observables = [
            this.showLoading$(this.recipientsGrid, '_commons.loading'),
            ...this.editedRecipients.map(rec => this.notifsService.updateEnvironmentRecipient(rec.environmentId, rec.id, rec.data)),
        ];

        forkJoin(observables).pipe(
            mergeMap(() => this.translateService.get('alerts.msgUpdateRecipientsComplete')),
            finalize(() => {
                this.editedRecipients = [];
                this.recipientsGrid.instance.endCustomLoading();
            }),
        )
            .subscribe(
                message => notify(message, 'success', 3000),
                err => this.handleError(err),
            );
    }

    /**
     * Deletes a recipient after confirmation.
     * @param recipient Recipient to delete.
     */
    public deleteRecipient(e, recipient: IOcbEnvironmentRecipient): void {
        e.event.stopPropagation();
        forkJoin(
            this.showLoading$(this.recipientsGrid, '_commons.loading'),
            this.translateService.get('alerts.msgDeleteRecipientConfirm', { name: recipient.displayName ? recipient.displayName : recipient.emailAddress }),
            this.translateService.get('alerts.msgDeleteRecipientConfirmTitle'),
        ).pipe(
            mergeMap(messages => fromPromise(confirm(messages[1], messages[2]))),
            filter(accepted => accepted),
            mergeMap(_ => this.notifsService.deleteEnvironmentRecipient(recipient.environmentId, recipient.id)),
            tap(_ => {
                const index = this.recipients.indexOf(recipient);
                this.recipients.splice(index, 1);
            }),
            mergeMap(_ => this.translateService.get('alerts.msgDeleteRecipientComplete')),
            finalize(() => this.recipientsGrid.instance.endCustomLoading()),
        ).subscribe(
            message => notify(message, 'success', 3000),
            err => this.handleError(err),
        );
    }

    public displayRecipientName(recipient: IOcbEnvironmentRecipient): string {
        return recipient.displayName ? recipient.displayName : recipient.emailAddress;
    }

    public displayEnvironmentLogType(type: string): string {
        return this.environmentLogTypesLabels[type];
    }

    public displayFileTransferLogType(type: string): string {
        return this.fileTransferLogTypesLabels[type];
    }

    public displayRemotePlatformLogType(type: string): string {
        return this.remotePlatformLogTypesLabels[type];
    }

    /**
     * Load data
     */
    private loadData(): void {
        // Load notification groups
        this.showLoading$(this.groupsGrid, '_commons.loading')
            .pipe(
                mergeMap(_ => this.notifsService.getNotificationGroups(this.environmentId)),
                finalize(() => this.groupsGrid.instance.endCustomLoading()),
            )
            .subscribe(
                res => this.groups = res,
                err => this.handleError(err),
            );

        // Load recipients
        this.showLoading$(this.recipientsGrid, '_commons.loading')
            .pipe(
                mergeMap(_ => this.notifsService.getEnvironmentRecipients(this.environmentId)),
                finalize(() => this.recipientsGrid.instance.endCustomLoading()),
            )
            .subscribe(
                res => this.recipients = res,
                err => this.handleError(err),
            );

        // Get available enum values
        this.environmentLogTypes = Object.keys(OcbEnvironmentLogTypes).filter(key => typeof (OcbEnvironmentLogTypes[key]) === 'number');
        this.fileTransferLogTypes = Object.keys(OcbFileTransferLogTypes).filter(key => typeof (OcbFileTransferLogTypes[key]) === 'number');
        this.remotePlatformLogTypes = Object.keys(OcbRemotePlatformLogTypes).filter(key => typeof (OcbRemotePlatformLogTypes[key]) === 'number');
    }

    /**
     * Mark a recipient as edited
     * @param recipient Edited recipient
     */
    private setEditedRecipient(recipient: IOcbEnvironmentRecipient): void {
        // Create a CU object and copy current data
        const cuRecipient = {
            id: recipient.id,
            environmentId: recipient.environmentId,
            data: {
                emailAddress: recipient.emailAddress,
                userId: recipient.user ? recipient.user.id : undefined,
                notificationGroupId: recipient.notificationGroupId,
                environmentLogTypesEnums: recipient.environmentLogTypesEnums,
                fileTransferLogTypesEnums: recipient.fileTransferLogTypesEnums,
                remotePlatformLogTypesEnums: recipient.remotePlatformLogTypesEnums,
            },
        };

        const editIndex = this.editedRecipients.findIndex(cuRec => cuRec.id === cuRecipient.id);
        if (editIndex > -1) {
            this.editedRecipients[editIndex] = cuRecipient;
        }
        else {
            this.editedRecipients.push(cuRecipient);
        }
    }

    // SHARED //

    private mapDataToGroup(data: INotificationGroupData): IOcbNotificationGroup {
        const emailRecipients = data.emails.map(email => {
            return { emailAddress: email };
        });

        const employeeRecipients = data.users.map(user => {
            return { userId: user.id };
        });

        return {
            id: data.id,
            name: data.name,
            environmentId: data.environmentId,
            recipients: [].concat(emailRecipients, employeeRecipients),
        };
    }

    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showLoading$(grid: DxDataGridComponent, messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => {
                if (grid != null) {
                    grid.instance.beginCustomLoading(`${message}...`);
                }
            }),
        );
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            }
            else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            }
        }
        else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}

interface INotificationGroupData {
    id?: string;
    environmentId: string;
    name: string;
    emails?: string[];
    users?: IOcbUser[];
}

interface IEnvironmentRecipientUpdateObject {
    id: string;
    environmentId: string;
    data: IOcbEnvironmentRecipient;
}
